// Employee week view — mobile-first
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import { PageHeader, Chip, EmptyState } from "@/components/ui-bits";
import { ChevronLeft, ChevronRight, CalendarDays, Users as UsersIcon, Share2, PlaneTakeoff, Wallet, PartyPopper, MapPin, Clock } from "lucide-react";
import { Modal } from "@/pages/Employees";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ManagerOnlineHint from "@/components/ManagerOnlineHint";
import useTodayISO from "@/lib/useTodayISO";
import ShiftCard from "@/components/ShiftCard";
import PaperPlaneEmpty from "@/components/PaperPlaneEmpty";
import SuccessCheckmark from "@/components/SuccessCheckmark";

import { toISO as fmt, todayISO, startOfWeek, addDays } from "@/lib/dates";

export default function MyRoster() {
  const { t, lang } = useI18n();
  const locale = { nl: "nl-NL", en: "en-GB", ar: "ar" }[lang] || "nl-NL";
  const { user } = useAuth();
  // Re-renders isToday chips + hero when midnight ticks over.
  // eslint-disable-next-line no-unused-vars
  const _todayISO = useTodayISO();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [shifts, setShifts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [today, setToday] = useState(null);
  // Phase 9B.3b1a — the /api/my/next-shift endpoint fills the gap where the
  // in-window computation cannot see beyond the currently loaded week.
  const [remoteNextShift, setRemoteNextShift] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [swapShift, setSwapShift] = useState(null);
  const [swapForm, setSwapForm] = useState({ message: "" });
  const [swapSuccess, setSwapSuccess] = useState(null); // label for checkmark overlay
  const [loading, setLoading] = useState(true);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const start = fmt(days[0]); const end = fmt(days[6]);

  const load = async () => {
    // Clear old shifts immediately so we never render last week's data under
    // this week's date headers (that caused a brief "ghost content" glitch).
    setLoading(true);
    setShifts([]);
    try {
      const [s, l, td, lb, ps] = await Promise.all([
        api.get(`/shifts?start=${start}&end=${end}`),
        api.get(`/locations`),
        api.get(`/today`),
        api.get(`/my/leave-balance`).catch(() => ({ data: null })),
        api.get(`/my/payroll-summary?period=month`).catch(() => ({ data: null })),
      ]);
      setShifts(s.data.filter((x) => x.employee_id));
      setLocations(l.data);
      setToday(td.data);
      setLeaveBalance(lb.data);
      setPayrollSummary(ps.data);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [start, end]);

  // Phase 9B.3b1a — fetch the real earliest future shift once on mount so
  // the hero also works when the user is on a past/future week or when
  // their next shift is more than a week away.
  useEffect(() => {
    let cancelled = false;
    api.get("/my/next-shift")
      .then(({ data }) => { if (!cancelled) setRemoteNextShift(data || null); })
      .catch(() => { if (!cancelled) setRemoteNextShift(null); });
    return () => { cancelled = true; };
  }, []);

  const locColor = (id) => locations.find((l) => l._id === id)?.color || "#0033A0";
  const locName = (id) => locations.find((l) => l._id === id)?.name || null;

  // Phase 9B.3a + 9B.3b1a — prefer the in-window shift if present (avoids a
  // second render pass on the common path) and fall back to the server-side
  // /my/next-shift result for weeks that don't contain the next shift.
  const nextShift = useMemo(() => {
    const nowIso = todayISO();
    const upcoming = shifts
      .filter((s) => s.date >= nowIso)
      .sort((a, b) => (a.date === b.date
        ? (a.start_time || "").localeCompare(b.start_time || "")
        : a.date.localeCompare(b.date)));
    return upcoming[0] || remoteNextShift || null;
  }, [shifts, remoteNextShift]);

  const submitSwap = async () => {
    try {
      await api.post("/swap-requests", { shift_id: swapShift._id, message: swapForm.message });
      setSwapSuccess(t("ok_swap_submitted"));
      setTimeout(() => {
        setSwapShift(null);
        setSwapForm({ message: "" });
        setSwapSuccess(null);
      }, 1100);
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  const shiftsByDate = useMemo(() => {
    const m = {}; for (const s of shifts) (m[s.date] = m[s.date] || []).push(s); return m;
  }, [shifts]);

  // Selected day for compact "one-day-at-a-time" view.
  const todayKey = todayISO();

  return (
    <div data-testid="page-my-roster">
      <ManagerOnlineHint />
      <PageHeader
        eyebrow={t("my_roster")}
        title={t("upcoming_shifts")}
        subtitle={t("my_roster_subtitle")}
        actions={
          <div className="flex items-center gap-1 border hairline rounded-xl">
            <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="btn btn-ghost !border-0 !h-9" data-testid="my-week-prev"><ChevronLeft size={14} /></button>
            <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="btn btn-ghost !border-0 !h-9">{t("today")}</button>
            <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="btn btn-ghost !border-0 !h-9" data-testid="my-week-next"><ChevronRight size={14} /></button>
          </div>
        }
      />

      {/* Phase 9B.3a — Next Shift hero. Mobile-first agenda kick-off:
          gives the employee a single unambiguous answer to "when do I work next?"
          without having to scan the whole week grid.
          Feb 2026 (Mega Session 2) — deduplicated: hide the hero whenever the
          next shift is already visible in the day-cards below (same info
          otherwise appears twice on one screen). It reappears when the user
          navigates to a different week or the next shift is beyond the
          currently visible window. */}
      {nextShift && (nextShift.date < start || nextShift.date > end) && (() => {
        const nsDate = new Date(nextShift.date + "T00:00:00");
        const nsLocName = locName(nextShift.location_id);
        const nsColor = locColor(nextShift.location_id);
        const isToday = nextShift.date === todayISO();
        return (
          <section className="mb-4" data-testid="my-roster-next-shift">
            <div
              className="card p-5 md:p-6 border-l-4"
              style={{ borderLeftColor: nsColor, background: "hsl(var(--primary-soft) / 0.35)" }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--primary))]" data-testid="next-shift-eyebrow">
                    {isToday ? (t("next_shift_today_eyebrow") || t("today")) : t("next_shift_eyebrow")}
                  </div>
                  <div className="font-display text-[20px] md:text-[22px] font-bold tracking-tight text-[hsl(var(--text-strong))] mt-1 leading-tight" data-testid="next-shift-date">
                    {nsDate.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}
                  </div>
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <div className="inline-flex items-center gap-1.5 text-[15px] font-semibold tabular-nums text-[hsl(var(--text))]" data-testid="next-shift-time">
                      <Clock size={14} strokeWidth={2} className="text-[hsl(var(--text-muted))]" />
                      {(nextShift.start_time || "").slice(0, 5)}–{(nextShift.end_time || "").slice(0, 5)}
                    </div>
                    {nsLocName && (
                      <div className="inline-flex items-center gap-1.5 text-[13px] text-[hsl(var(--text-muted))]" data-testid="next-shift-location">
                        <MapPin size={12} strokeWidth={2} />
                        {nsLocName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Vandaag met wie werk je + verjaardagen deze maand */}
      <TodayTeamStrip today={today} />

      {loading ? (
        <div className="flex flex-col gap-3" data-testid="my-roster-loading">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-2xl border hairline">
              <div className="skeleton w-10 h-10 rounded-xl shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-3 rounded-md w-1/3" />
                <div className="skeleton h-2.5 rounded-md w-1/5" />
              </div>
            </div>
          ))}
        </div>
      ) : shifts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 px-6" data-testid="my-roster-empty">
          <PaperPlaneEmpty size={180} />
          <h3 className="mt-6 font-display text-[20px] font-bold text-[hsl(var(--text-strong))] tracking-tight text-center">
            {t("empty_shifts_week_title") || "Nog geen diensten deze week"}
          </h3>
          <p className="mt-2 text-[13px] text-[hsl(var(--text-muted))] text-center max-w-[320px] leading-relaxed">
            {t("empty_shifts_week_hint") || "Zodra je manager je diensten plant, verschijnen ze hier."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {days.map((d) => {
            const key = fmt(d);
            const dayShifts = shiftsByDate[key] || [];
            const isToday = key === todayKey;
            const isPast = d < new Date(new Date().setHours(0,0,0,0));
            const isFree = dayShifts.length === 0;
            if (isFree) {
              // Compact one-liner for days off — much less visual weight than a full card.
              return (
                <div
                  key={key}
                  className={`flex items-center gap-3 px-4 py-2 rounded-2xl border hairline text-[13px] ${isToday ? "bg-[hsl(var(--primary-soft)_/_0.4)] border-[hsl(var(--primary)_/_0.25)]" : "bg-transparent opacity-70"}`}
                  data-testid={`my-day-${key}`}
                >
                  <div className={`w-8 h-8 rounded-lg grid place-items-center font-display font-bold text-[13px] shrink-0 ${isToday ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]"}`}>
                    {d.getDate()}
                  </div>
                  <div className="flex-1 min-w-0 flex items-baseline gap-2">
                    <span className={`font-semibold ${isToday ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--text-muted))]"}`}>
                      {d.toLocaleDateString(locale, { weekday: "long" })}
                    </span>
                    <span className="text-[11px] text-[hsl(var(--text-soft))] italic" data-testid={`my-day-free-${key}`}>
                      · {isPast ? (t("day_free_past") || "Vrije dag") : (t("day_free") || "Geen dienst")}
                    </span>
                  </div>
                  {isToday && <Chip variant="primary">{t("today")}</Chip>}
                </div>
              );
            }
            return (
              <div
                key={key}
                className={`card overflow-hidden transition-all ${isToday ? "ring-1 ring-[hsl(var(--primary))]" : ""}`}
                data-testid={`my-day-${key}`}
              >
                <div className={`px-4 py-3 border-b hairline flex items-center justify-between ${isToday ? "bg-[hsl(var(--primary-soft))]" : "bg-[hsl(var(--muted))]"}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl grid place-items-center font-display font-bold ${isToday ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--surface))] text-[hsl(var(--text-strong))] border hairline"}`}>
                      <span className="text-[16px] leading-none">{d.getDate()}</span>
                    </div>
                    <div>
                      <div className="eyebrow !text-[10px]">{d.toLocaleDateString(locale, { weekday: "long" })}</div>
                      <div className="text-[12px] text-[hsl(var(--text-muted))] font-medium">{d.toLocaleDateString(locale, { month: "long" })}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-[11px] font-mono tabular-nums text-[hsl(var(--text-muted))] hidden sm:block">
                      {dayShifts.length} {dayShifts.length === 1 ? t("shift_singular") || "dienst" : t("shifts") || "diensten"}
                    </div>
                    {isToday && <Chip variant="primary">{t("today")}</Chip>}
                  </div>
                </div>
                <div className="divide-y hairline">
                  {dayShifts.map((s) => (
                    <ShiftCard
                      key={s._id}
                      shift={s}
                      accentColor={locColor(s.location_id)}
                      onSwap={setSwapShift}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {swapShift && (
        <Modal title={t("swap_shift")} onClose={() => setSwapShift(null)}>
          <div className="relative">
            {swapSuccess && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="absolute inset-0 z-10 grid place-items-center bg-[hsl(var(--surface))] rounded-2xl py-6"
                data-testid="swap-success"
              >
                <SuccessCheckmark label={swapSuccess} />
              </motion.div>
            )}
            <div className="text-sm mb-3 text-[hsl(var(--text-muted))]">{swapShift.date} · {swapShift.start_time}–{swapShift.end_time}</div>
            <label className="flex flex-col gap-1"><span className="eyebrow">{t("message")}</span>
              <textarea data-testid="swap-message" rows={3} value={swapForm.message} onChange={(e) => setSwapForm({ message: e.target.value })} className={inp + " !h-auto py-2"} />
            </label>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setSwapShift(null)} className="btn btn-ghost">{t("cancel")}</button>
              <button onClick={submitSwap} className="btn btn-primary" data-testid="swap-submit">{t("send")}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
// -------------------- Vandaag werk je met --------------------
function TodayTeamStrip({ today }) {
  const { t } = useI18n();
  if (!today) return null;
  const colleagues = today.colleagues_today || [];
  if (colleagues.length === 0) return null;

  return (
    <section className="mb-5" data-testid="today-team-strip">
      <div className="card p-5 max-w-2xl" data-testid="today-colleagues">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center">
            <UsersIcon size={16} strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))]">{t("today_work_with")}</div>
            <div className="text-[11px] text-[hsl(var(--text-muted))]">{colleagues.length} {colleagues.length === 1 ? t("colleague") : t("colleagues")}</div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {colleagues.map((c) => (
            <div key={c._id + (c.start_time || "")} className="flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-[hsl(var(--muted))]" data-testid={`col-${c._id}`}>
              <div className="w-9 h-9 rounded-full grid place-items-center text-white text-[11px] font-bold shrink-0"
                   style={{ background: c.location_color }}>
                {c.initials || "?"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">{c.name}</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] truncate">
                  {[c.job_title, c.location].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="text-[11px] font-mono text-[hsl(var(--text-muted))] tabular-nums shrink-0">
                {c.start_time?.slice(0, 5)}–{c.end_time?.slice(0, 5)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
const inp = "card h-10 px-3 text-sm rounded-xl w-full";

// -------------------- Personal Insight Strip (Fase 1 - Feb 2026) --------------------
function MyInsightStrip({ leaveBalance, payrollSummary, today, t, lang }) {
  const bdays = [
    ...(today?.birthdays_today || []).map((b) => ({ ...b, when: t("today"), highlight: true })),
    ...(today?.birthdays_upcoming || []),
  ].slice(0, 8);

  const lb = leaveBalance || { allocated: 25, remaining: 0, pending_days: 0, used_pct: 0 };
  const remaining = lb.remaining ?? 0;
  const lbAccent = remaining <= 3 ? "hsl(0 75% 55%)" : remaining <= 8 ? "hsl(38 92% 50%)" : "hsl(160 65% 42%)";

  const ps = payrollSummary || { total_amount: 0, total_hours: 0, currency: "EUR", has_rate: false };
  const money = new Intl.NumberFormat(lang === "en" ? "en-GB" : "nl-NL", {
    style: "currency", currency: ps.currency || "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(ps.total_amount || 0);
  const payAccent = "hsl(217 91% 55%)";

  return (
    <section className="mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-insight-strip">
      {/* Leave balance */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="card card-lift p-4 flex items-center gap-3"
        data-testid="mi-leave-balance"
      >
        <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${lbAccent}18`, color: lbAccent }}>
          <PlaneTakeoff size={17} strokeWidth={1.9} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="eyebrow !text-[10px]">{t("w_my_leave_balance")}</div>
          <div className="font-display text-[22px] leading-none tabular-nums font-bold text-[hsl(var(--text-strong))] mt-1">
            {remaining}<span className="text-[13px] text-[hsl(var(--text-muted))] font-medium ml-0.5">/{lb.allocated}</span>
            {lb.pending_days > 0 && (
              <span className="ml-2 text-[10px] chip chip-warning !inline-flex">+{lb.pending_days}</span>
            )}
          </div>
          <div className="mt-2 h-1 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${Math.min(100, lb.used_pct || 0)}%`, background: lbAccent }} />
          </div>
        </div>
      </motion.div>

      {/* Expected pay */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="card card-lift p-4 flex items-center gap-3"
        data-testid="mi-expected-pay"
      >
        <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${payAccent}18`, color: payAccent }}>
          <Wallet size={17} strokeWidth={1.9} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="eyebrow !text-[10px]">{t("w_my_expected_pay")}</div>
          {ps.has_rate ? (
            <>
              <div className="font-display text-[22px] leading-none tabular-nums font-bold text-[hsl(var(--text-strong))] mt-1 truncate">
                {money}
              </div>
              <div className="text-[10.5px] text-[hsl(var(--text-muted))] mt-1 tabular-nums">
                {(ps.total_hours || 0).toFixed(1)}u {t("planned_hours_short") || "gepland"} · {ps.shifts_count || 0}×
              </div>
            </>
          ) : (
            <div className="text-[13px] text-[hsl(var(--text-muted))] italic mt-1">
              {t("payroll_no_rate")}
            </div>
          )}
        </div>
      </motion.div>

      {/* Birthday strip */}
      {bdays.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="card p-4 sm:col-span-2 lg:col-span-1"
          data-testid="mi-birthdays"
        >
          <div className="flex items-center gap-2 mb-2.5">
            <div className="w-8 h-8 rounded-xl bg-[hsl(45_95%_92%)] text-[hsl(38_92%_45%)] grid place-items-center">
              <PartyPopper size={15} strokeWidth={1.9} />
            </div>
            <div className="min-w-0">
              <div className="eyebrow !text-[10px]">{t("w_birthdays_this_week")}</div>
              <div className="text-[11px] text-[hsl(var(--text-muted))]">{bdays.length} {bdays.length === 1 ? t("colleague") : t("colleagues")}</div>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1" data-testid="mi-birthdays-strip">
            {bdays.slice(0, 5).map((b) => (
              <div key={b._id} className="shrink-0 flex flex-col items-center gap-1 min-w-[64px]">
                <div className="relative">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(217_91%_45%)] text-white text-[11px] font-bold grid place-items-center">
                    {b.initials || (b.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  {b.highlight && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[hsl(45_95%_60%)] text-[9px] grid place-items-center border-2 border-[hsl(var(--surface))]">🎂</span>
                  )}
                </div>
                <div className="text-[10px] font-semibold text-[hsl(var(--text-strong))] truncate max-w-[64px]">{b.name?.split(" ")[0]}</div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </section>
  );
}
